import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
  Box,
  Text,
  Button,
  Spinner,
  Center,
  HStack,
  Badge,
  Avatar,
  Divider,
  useToast,
} from '@chakra-ui/react';
import { IoSearch, IoCheckmarkCircle } from 'react-icons/io5';
import axios from 'axios';
import { useTranslation } from 'next-i18next';

export interface TransactionSelectorItem {
  _id: string;
  counterparty: string;
  transactionDate: string;
  amount: number;
  currency?: string;
  reference?: string;
  clearingAccount?: {
    name: string;
  };
  linkedToInvoice?: boolean;
}

interface TransactionSelectorDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (transaction: TransactionSelectorItem) => void;
  apiUrl: string;
  session: {
    accessToken: string;
  };
  excludeTransactionId?: string;
  excludeTransactionIds?: string[];
  showLinkedOnly?: boolean;
  showUnlinkedOnly?: boolean;
}

const TransactionSelectorDrawer: React.FC<TransactionSelectorDrawerProps> = ({
  isOpen,
  onClose,
  onSelect,
  apiUrl,
  session,
  excludeTransactionId,
  excludeTransactionIds = [],
  showLinkedOnly = false,
  showUnlinkedOnly = false,
}) => {
  const { t } = useTranslation('common');
  const toast = useToast();

  const [transactions, setTransactions] = useState<TransactionSelectorItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [appliedSearchText, setAppliedSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Load transactions
  const loadTransactions = useCallback(
    async (pageNumber = 1, searchQuery = '') => {
      if (!session?.accessToken) return;

      if (pageNumber === 1) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        let queryString = `?page=${pageNumber}&pageSize=25`;

        // Add search filter
        if (searchQuery) {
          queryString += `&counterparty=${encodeURIComponent(searchQuery)}`;
        }

        // Add link status filter
        if (showLinkedOnly) {
          queryString += `&linkStatus=linked`;
        } else if (showUnlinkedOnly) {
          queryString += `&linkStatus=unlinked`;
        }

        const response = await axios.get(`${apiUrl}/transactions${queryString}`, {
          headers: { Authorization: `Bearer ${session.accessToken}` },
        });

        let fetchedTransactions = response.data || [];

        const allExcluded = [...excludeTransactionIds];
        if (excludeTransactionId) allExcluded.push(excludeTransactionId);
        if (allExcluded.length > 0) {
          fetchedTransactions = fetchedTransactions.filter(
            (tx: TransactionSelectorItem) => !allExcluded.includes(tx._id)
          );
        }

        // Mark transactions with linked status
        const transactionsWithLinks = fetchedTransactions.map((transaction: TransactionSelectorItem) => ({
          ...transaction,
          linkedToInvoice: !!transaction.linkedToInvoice,
        }));

        if (pageNumber === 1) {
          setTransactions(transactionsWithLinks);
        } else {
          setTransactions((prev) => [...prev, ...transactionsWithLinks]);
        }

        setHasMore(fetchedTransactions.length === 25);
        setCurrentPage(pageNumber);
      } catch (error) {
        console.error('Error loading transactions:', error);
        toast({
          title: t('components.transactions.errors.loadError') || 'Error loading transactions',
          description:
            (error as any).response?.data?.message ||
            t('components.transactions.errors.loadErrorDescription') ||
            'Could not load transactions',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [session, apiUrl, excludeTransactionId, excludeTransactionIds, showLinkedOnly, showUnlinkedOnly, toast, t]
  );

  // Handle search on Enter key
  const handleSearch = () => {
    setAppliedSearchText(searchText);
    setCurrentPage(1);
    loadTransactions(1, searchText);
  };

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isLoadingMore || !hasMore) return;

    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    // Load more when user scrolls to 80% of the content
    if (scrollTop + clientHeight >= scrollHeight * 0.8) {
      loadTransactions(currentPage + 1, appliedSearchText);
    }
  }, [currentPage, appliedSearchText, hasMore, isLoadingMore, loadTransactions]);

  // Load transactions when drawer opens
  useEffect(() => {
    if (isOpen) {
      setSearchText('');
      setAppliedSearchText('');
      setCurrentPage(1);
      loadTransactions(1, '');
    }
  }, [isOpen, loadTransactions]);

  // Setup scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  // Handle transaction selection
  const handleSelectTransaction = (transaction: TransactionSelectorItem) => {
    onSelect(transaction);
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} placement="right" size="md" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          {t('components.transactions.selectTransaction') || 'Select Transaction'}
        </DrawerHeader>

        <DrawerBody p={0}>
          {/* Search Input */}
          <Box p={4} borderBottomWidth="1px">
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <IoSearch color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder={
                  t('components.transactions.searchPlaceholder') || 'Search by counterparty name...'
                }
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: 'gray.400' }}
                _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 1px rgb(49, 206, 167)' }}
              />
            </InputGroup>
            {searchText && (
              <Button
                mt={2}
                size="sm"
                colorScheme="rekening"
                leftIcon={<IoSearch />}
                onClick={handleSearch}
                width="full"
              >
                {t('components.transactions.filter') || 'Search'}
              </Button>
            )}
          </Box>

          {/* Transactions List */}
          <Box
            ref={scrollContainerRef}
            overflowY="auto"
            maxH="calc(100vh - 200px)"
            p={4}
          >
            {isLoading && transactions.length === 0 ? (
              <Center h="200px">
                <VStack>
                  <Spinner size="xl" />
                  <Text>{t('components.transactions.loadingTransactions') || 'Loading transactions...'}</Text>
                </VStack>
              </Center>
            ) : transactions.length === 0 ? (
              <Center h="200px">
                <Text color="gray.500">
                  {appliedSearchText
                    ? t('components.transactions.noTransactions') || 'No transactions found'
                    : t('components.transactions.noTransactions') || 'No transactions available'}
                </Text>
              </Center>
            ) : (
              <VStack spacing={2} align="stretch">
                {transactions.map((transaction) => (
                  <Box
                    key={transaction._id}
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    cursor="pointer"
                    _hover={{
                      bg: 'gray.50',
                      borderColor: 'blue.300',
                      transform: 'translateY(-1px)',
                      boxShadow: 'sm',
                    }}
                    transition="all 0.2s"
                    onClick={() => handleSelectTransaction(transaction)}
                  >
                    <HStack spacing={3} align="start">
                      <Avatar size="sm" name={transaction.counterparty} />
                      <Box flex={1} minW={0}>
                        <HStack spacing={2} mb={1}>
                          <Text fontWeight="bold" fontSize="sm" noOfLines={1}>
                            {transaction.counterparty}
                          </Text>
                          {transaction.linkedToInvoice && (
                            <Badge colorScheme="green" size="sm">
                              {t('components.transactions.linked') || 'Linked'}
                            </Badge>
                          )}
                        </HStack>
                        <Text fontSize="xs" color="gray.600" mb={1}>
                          {new Date(transaction.transactionDate).toLocaleDateString('de-DE')}
                        </Text>
                        {transaction.reference && (
                          <Text fontSize="xs" color="gray.500" noOfLines={1}>
                            {transaction.reference}
                          </Text>
                        )}
                        <HStack spacing={2} mt={1}>
                          <Text fontWeight="bold" fontSize="sm" color="green.600">
                            {transaction.amount?.toFixed(2)} {transaction.currency || '€'}
                          </Text>
                          {transaction.clearingAccount?.name && (
                            <>
                              <Text fontSize="xs" color="gray.400">
                                •
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {transaction.clearingAccount.name}
                              </Text>
                            </>
                          )}
                        </HStack>
                      </Box>
                      <IoCheckmarkCircle
                        size={20}
                        color="gray.300"
                        style={{ flexShrink: 0, marginTop: '4px' }}
                      />
                    </HStack>
                  </Box>
                ))}
                {isLoadingMore && (
                  <Center py={4}>
                    <Spinner size="sm" />
                    <Text ml={2} fontSize="sm" color="gray.500">
                      {t('components.transactions.loadingMore') || 'Loading more...'}
                    </Text>
                  </Center>
                )}
                {!hasMore && transactions.length > 0 && (
                  <Center py={4}>
                    <Text fontSize="sm" color="gray.500">
                      {t('components.transactions.noMoreTransactions') || 'No more transactions'}
                    </Text>
                  </Center>
                )}
              </VStack>
            )}
          </Box>
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px">
          <Button variant="outline" mr={3} onClick={onClose}>
            {t('components.transactions.close') || 'Close'}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default TransactionSelectorDrawer;

